/* eslint-disable @typescript-eslint/no-empty-function */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '../../../../../base/api';
import { FileToUpload, UploadedFile } from '../../../../../base/components/Forms/FileUpload';
import { Rate } from '../../../../../base/enums';
import { Area, City } from '../../../../../base/interfaces/config';
import { SetFieldParams } from '../../../../../base/interfaces/params';
import { Speciality } from '../../interfaces';

export type InitialVacancyDataParams = Omit<Fields, 'imagesToUpload'>;

export interface Fields {
    name: string;
    forRecruiters: boolean;
    forWorkers: boolean;
    suitableForMen: boolean;
    suitableForWomen: boolean;
    suitableForCouple: boolean;
    suitableForStudents: boolean;
    rateType: Rate;
    rateFrom: string;
    rateTo: string;
    hoursPerMonthFrom: string;
    hoursPerMonthTo: string;
    currencyId: number;
    isHotVacancy: boolean;
    isExperienceNeeded: boolean;
    isPassportOnly: boolean;
    isAccomodationProvided: boolean;
    isFoodProvided: boolean;
    isTransferProvided: boolean;
    isClothesProvided: boolean;
    isSeasonalWork: boolean;
    isRemoteWork: boolean;
    isQuickApplyAvailable: boolean;
    city: City | null;
    description: string;
    images: UploadedFile[];
    imagesToUpload: FileToUpload[];
    requiredLanguageId: number | null;
    specialities: Speciality[];
    areas: Area[];
    workersCount: string;
    updatedAt: number | null;
    views: number | null;
}

export interface VacancyFormState {
    step: number;
    vacancyId: string | null;
    fields: Fields;
    errors: IError | null;
    duplicate: boolean;
    shouldScrollToError: boolean;
}

const fieldsInitialState: Fields = {
    name: '',
    forRecruiters: false,
    forWorkers: false,
    suitableForMen: false,
    suitableForWomen: false,
    suitableForCouple: false,
    suitableForStudents: false,
    rateType: Rate.Hourly,
    rateFrom: '',
    rateTo: '',
    hoursPerMonthFrom: '',
    hoursPerMonthTo: '',
    currencyId: 1,
    isHotVacancy: false,
    isExperienceNeeded: false,
    isPassportOnly: false,
    isAccomodationProvided: false,
    isFoodProvided: false,
    isTransferProvided: false,
    isClothesProvided: false,
    isSeasonalWork: false,
    isRemoteWork: false,
    isQuickApplyAvailable: false,
    city: null,
    description: '',
    images: [],
    imagesToUpload: [],
    requiredLanguageId: null,
    specialities: [],
    areas: [],
    workersCount: '1',
    updatedAt: null,
    views: null,
};

const initialState: VacancyFormState = {
    step: 1,
    vacancyId: null,
    fields: fieldsInitialState,
    errors: null,
    duplicate: false,
    shouldScrollToError: false,
};

export const vacancyFormSlice = createSlice({
    name: 'employer__vacancy-form',
    initialState,
    reducers: {
        refreshVacancyDataToInitialState: (state) => {
            state.step = 1;
            state.vacancyId = null;
            state.fields = fieldsInitialState;
            state.errors = null;
            state.duplicate = false;
            state.shouldScrollToError = false;
        },
        setInitialVacancyData: (state, action: PayloadAction<InitialVacancyDataParams>) => {
            state.fields = {
                ...state.fields,
                ...action.payload,
            };
        },
        setVacancyId: (state, action: PayloadAction<string>) => {
            state.vacancyId = action.payload;
        },
        setDuplicate: (state, action: PayloadAction<boolean>) => {
            state.duplicate = action.payload;
        },
        setStep: (state, action: PayloadAction<number>) => {
            state.step = action.payload;
        },
        nextStep: () => {},
        init: {
            reducer: () => {},
            prepare: (vacancyId?: string) => ({ payload: vacancyId }),
        },

        setField: (state, action: PayloadAction<SetFieldParams>) => {
            state.fields = {
                ...state.fields,
                [action.payload.id]: action.payload.value,
            };
            if (state.errors === null) {
                return;
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [action.payload.errorGroupName || action.payload.id]: removedError, ...errors } = state.errors;
            state.errors = errors;
        },
        setErrors: (state, action: PayloadAction<IError>) => {
            if (state.errors) {
                state.errors = {
                    ...state.errors,
                    ...action.payload,
                };
            } else {
                state.errors = action.payload;
            }
        },
        setShouldScrollToError: (state, action: PayloadAction<boolean>) => {
            state.shouldScrollToError = action.payload;
        },
        deleteImageById: {
            reducer: () => {},
            prepare: (vacancyId: string, imageId: number) => ({ payload: { vacancyId, imageId } }),
        },
    },
});

export const vacancyFormActions = vacancyFormSlice.actions;
export const vacancyFormReducer = vacancyFormSlice.reducer;
